<!-- Part of the SPARKL educational activity system, Copyright 2025 by Common Good Learning Tools -->
<template>
	<k-dialog
		v-model="dialog_open"
		:max-width="dialog_max_width"
		:hide-overlay="shifted"
		:no-click-animation="shifted"
		:retain-focus="!shifted"
		:content-class="shifted ? 'k-resource-editor-dialog-shifted' : ''"
		persistent
		scrollable
	>
		<k-card class="k-resource-editor-outer">

			<k-card-title style="border-bottom:1px solid #999" class="pa-2">
				<b class="ml-2" style="font-weight:900" v-html="header_text" tabindex="0" data-k-dialog-initial-focus="true"></b>
				<k-spacer/>
				<k-tooltip top><template v-slot:activator="{on}"><k-btn v-if="show_shift_resource_button" v-on="on" class="ml-3" fab x-small dark color="secondary" @click="$emit('shift_resource')"><k-icon small>fas {{shifted ? 'fa-angles-left' : 'fa-angles-right'}}</k-icon></k-btn></template>{{shifted ? 'Move Back to Center' : 'View Resource Side-by-Side'}}</k-tooltip>
				<k-btn small class="ml-3" color="secondary" @click="$emit('edit_resource_cancel')" tabindex="7"><k-icon small class="mr-2">fas fa-times</k-icon>Cancel</k-btn>
				<k-btn v-if="resource_type!='none'" small class="ml-3" color="primary" @click="save_resource" tabindex="6"><k-icon small class="mr-2">fas fa-check</k-icon>Save Resource</k-btn>
			</k-card-title>

			<k-card-text class="pt-3 pb-1" style="background-color:#eee">
				<div class="mt-4">
					<div class="d-flex align-center mb-2">
						<b v-if="is_new_resource&&edited_resource.type=='none'" class="grey--text text--darken-3 mr-2">Choose a media type for your new resource:</b>
						<k-select v-model="edited_resource.type" :items="resource_types" label="Media Type" tabindex="1" outlined hide-details dense background-color="#fff" :style="(edited_resource.type=='none'||edited_resource.type=='html')?'max-width:320px':'max-width:180px'" @change="resource_type_selected"></k-select>
						<k-select v-show="resource_type=='assessment'" v-model="edited_resource.assessment_type" :items="assessment_types" label="Assessment Type" tabindex="2" outlined hide-details dense background-color="#fff" style="max-width:180px" class="ml-2"></k-select>
						<k-text-field v-show="resource_type!='none'&&resource_type!='html'" hide-details outlined clearable :label="url_field_label" v-model="edited_resource.url" background-color="#fff" dense tabindex="3" class="ml-2"></k-text-field>
						<k-btn v-show="resource_type=='upload'" @click="upload_file" class="ml-2" fab small color="light-blue" dark><k-icon>fas fa-file-upload</k-icon></k-btn>
					</div>

					<k-textarea v-show="resource_type!='none'" class="my-4" hide-details outlined label="Resource title/description" v-model="edited_resource.description" background-color="#fff" auto-grow dense rows="1" tabindex="4"></k-textarea>

					<div v-if="resource_type!='none'" class="mb-3">
						<div class="mb-1"><b>Long Description (optional):</b></div>
						<froala-wrapper v-model="edited_resource.long_description"  :config="long_description_editor_config"/>
					</div>

					<div v-if="resource_type=='assessment'" class="d-flex mb-3">
						<k-text-field v-if="edited_resource.assessment_type!='writable'" hide-details outlined clearable label="Assessment Blueprint URL (optional)" class="mx-1" v-model="blueprint_url" background-color="#fff" dense></k-text-field>
						<k-text-field hide-details outlined clearable :label="edited_resource.assessment_type=='writable'?'Writable Booklet URL (optional)':'Teacher Test Booklet URL (optional)'" class="mx-1" v-model="paper_url" background-color="#fff" dense></k-text-field>
						<k-text-field hide-details outlined clearable label="Student Test Booklet URL (optional)" class="mx-1" v-model="student_booklet_url" background-color="#fff" dense></k-text-field>
					</div>

					<div class="mb-3" v-if="resource_type=='html'">
						<div class="mb-1"><b>Edit the resource HTML here:</b> (to edit HTML directly, click the <k-icon color="#000" small>fas fa-code-simple</k-icon> button)</div>
						<froala-wrapper ref="froala_wrapper" :config="html_resource_editor_config" v-model="resource_html"/>
					</div>

					<div class="my-4 d-flex justify-center align-center flex-wrap">
						<div v-if="show_teacher_facing_control"><k-btn-toggle dense active-class="k-toggle-btn-active-class" class="k-toggle-btn" v-model="edited_resource.teacher_facing" mandatory>
							<k-btn small light :value="false" style="letter-spacing:0" @click.stop="set_teacher_facing_default(false)">Student-Facing</k-btn>
							<k-btn small light :value="true" style="letter-spacing:0" @click.stop="set_teacher_facing_default(true)">Teacher-Facing</k-btn>
						</k-btn-toggle></div>

						<k-btn small color="#666" dark class="ml-4 k-tight-btn" @click="additional_metadata_showing=!additional_metadata_showing">Additional Tagging Options <k-icon small class="ml-2">fas fa-caret-{{additional_metadata_showing?'up':'down'}}</k-icon></k-btn>
					</div>

					<!-- below are shown if the user clicks to see additional options -->
					<div v-show="additional_metadata_showing">

						<!-- STANDARDS -->
						<div class="mb-4">
							<div class="d-flex align-center">
								<div><b>Standards:</b></div>
								<k-btn :text="new_standard_is_secondary" x-small color="primary" dark class="ml-2 k-nocaps-btn k-tight-btn" @click="align_to_standard('primary')"><k-icon small class="mr-1">fas fa-plus</k-icon> Add PRIMARY Alignment</k-btn>
								<k-btn :text="!new_standard_is_secondary" x-small color="#666" dark class="ml-2 k-nocaps-btn k-tight-btn" @click="align_to_standard('secondary')"><k-icon small class="mr-1">fas fa-plus</k-icon> Add SECONDARY Alignment</k-btn>
								<k-spacer/>
								<k-btn v-if="edited_resource.standards.length==0&&$store.state.lst.resource_editor_standards.length>0" x-small color="secondary" class="k-nocaps-btn k-tight-btn" @click="use_last_selected_standards">Use Last-Selected Standards</k-btn>
							</div>
							<draggable v-bind="drag_options" v-model="edited_resource.standards" @end="drag_complete" class="d-flex flex-wrap align-center justify-center my-1">
								<CASEItemBtn v-for="(s) in edited_resource.standards" :key="s.identifier" :item="s" @click="align_to_standard(s)" @delete="remove_standard(s)" :btn_color="s.btn_color(edited_resource.standards)" small show_move_icon show_delete_icon :outer_class="'ma-1'" />
							</draggable>
						</div>

						<!-- Settings implemented for for HenryConnects -->
						<div class="d-flex justify-center align-center flex-wrap" v-if="show_teacher_facing_control">
							<div v-if="!edited_resource.teacher_facing&&site_config.show_target_students_menu_in_resource_editor=='yes'" class="mb-3">
								Target Students:
								<k-menu bottom left>
									<template v-slot:activator="{on}"><span v-on="on" class="ml-1">
										<k-btn small v-if="edited_resource.target_students=='all'" color="amber lighten-2">All Students</k-btn>
										<k-btn small v-if="edited_resource.target_students=='ese'" dark color="teal">ESE Students</k-btn>
										<k-btn small v-if="edited_resource.target_students=='adv'" dark color="purple">Advanced Learners</k-btn>
									</span></template>
									<k-list dense>
										<k-list-item @click="edited_resource.target_students='all'"><k-list-item-title>All Students</k-list-item-title></k-list-item>
										<k-list-item @click="edited_resource.target_students='ese'"><k-list-item-title>ESE Students</k-list-item-title></k-list-item>
										<k-list-item @click="edited_resource.target_students='adv'"><k-list-item-title>Advanced Learners</k-list-item-title></k-list-item>
									</k-list>
								</k-menu>
							</div>

							<div v-if="site_config.show_special_resource_category_menu_in_resource_editor=='yes'" class="mb-3">
								<k-menu bottom left>
									<template v-slot:activator="{on}"><span v-on="on" class="ml-3">
										<k-btn small class="k-tight-btn k-nocaps-btn" v-if="edited_resource.lp_category==''">Special Resource Category…</k-btn>
										<k-btn small class="k-tight-btn k-nocaps-btn" v-if="edited_resource.lp_category=='unit_planning'" dark color="green">Unit Planning Resource</k-btn>
										<!-- <k-btn small class="k-tight-btn k-nocaps-btn" v-if="edited_resource.lp_category=='course_guidance'" dark color="cyan">Resources for Course Guidance</k-btn> -->
										<k-btn small class="k-tight-btn k-nocaps-btn" v-if="edited_resource.lp_category=='stem_resource'" dark color="lime">STEM Resource</k-btn>
										<k-btn small class="k-tight-btn k-nocaps-btn" v-if="edited_resource.lp_category=='leader_resource'" dark color="brown">Leader Resource</k-btn>
										<!-- <k-btn small class="k-tight-btn k-nocaps-btn" v-if="edited_resource.lp_category=='course_ebook'" dark color="indigo">Course Ebook</k-btn> -->
									</span></template>
									<k-list dense>
										<k-list-item @click="edited_resource.lp_category=''"><k-list-item-title>-- NONE --</k-list-item-title></k-list-item>
										<k-list-item @click="edited_resource.lp_category='unit_planning'"><k-list-item-title>Unit Planning Resource</k-list-item-title></k-list-item>
										<k-list-item @click="edited_resource.lp_category='leader_resource'"><k-list-item-title>Leader Resource</k-list-item-title></k-list-item>
										<!-- <k-list-item @click="edited_resource.lp_category='course_guidance'"><k-list-item-title>Resources for Course Guidance (shown in all units)</k-list-item-title></k-list-item> -->
										<k-list-item @click="edited_resource.lp_category='stem_resource'"><k-list-item-title>STEM Resource</k-list-item-title></k-list-item>
										<!-- <k-list-item @click="edited_resource.lp_category='course_ebook'"><k-list-item-title>Course Ebooks (shown in all units)</k-list-item-title></k-list-item> -->
									</k-list>
								</k-menu>
							</div>
							<!-- <div v-if="unit&&edited_resource.teacher_facing">
								<div class="mx-4"><k-checkbox class="mt-0 pt-0" :label="'Unit planning resource'" v-model="unit_planning" hide-details></k-checkbox></div>
							</div> -->
						</div>

						<!-- settings implemented for HenryConnects -->
						<div v-if="show_block_control||show_family_control||show_todo_control" class="d-flex mb-3"><k-spacer/>
							<div v-if="show_block_control" class="k-inline-checkbox-holder mx-2 mt-3">
								<k-checkbox class="ma-0 pa-0" :label="'Show in both block and traditional formats'" v-model="both_block_and_traditional" hide-details></k-checkbox>
								<div v-show="!both_block_and_traditional"><div class="d-flex align-center" style="font-size:14px"><k-spacer/>
									<div :style="edited_resource.block_or_traditional=='traditional'?'':'font-weight:bold'">Block</div><k-switch label="" v-model="block_or_traditional" hide-details class="mt-0 ml-3 mr-1"></k-switch><div :style="edited_resource.block_or_traditional!='traditional'?'':'font-weight:bold'">Traditional</div>
								<k-spacer/></div></div>
							</div>
							<div v-if="show_family_control" class="k-inline-checkbox-holder mx-2 mt-3"><k-checkbox class="ma-0 pa-0" :label="'Show to family members'" v-model="edited_resource.family_avail" hide-details></k-checkbox></div>
							<div v-if="show_todo_control" class="k-inline-checkbox-holder mx-2"><k-checkbox class="ma-0 pa-0" :label="'Require completion in PD collection'" v-model="edited_resource.todo" hide-details tabindex="5"></k-checkbox></div>
						<k-spacer/></div>

						<!-- metadata fields implemented for ALEX -->
						<div class="d-flex mb-3">
							<k-text-field hide-details outlined clearable label="Content Source" class="mr-2" v-model="content_source" background-color="#fff" dense></k-text-field>
							<k-select v-model="content_type" :items="content_type_options" label="Content Type" outlined hide-details dense background-color="#fff" style="max-width:210px" @change="content_type_changed"></k-select>
						</div>
						<div class="d-flex mb-3">
							<k-combobox
								v-model="license_text"
								:label="license_text_label"
								class="mr-1"
								background-color="#fff"
								:items="license_options"
								hide-details
								outlined
								clearable
								dense
							/>
							<k-text-field v-show="license_text_label=='License Description'" hide-details outlined clearable label="License URL" class="ml-1" v-model="license_uri" background-color="#fff" dense></k-text-field>
						</div>
						<div v-if="!edited_resource_has_extensions" class="text-center mb-3">
							<k-btn small color="secondary" class="k-tight-btn k-nocaps-btn" @click="use_last_selected_extensions">Use Last-Edited Content Source/Type/License Info</k-btn>
						</div>

						<!-- restricted access -->
						<div v-if="show_restricted_resources_control" class="d-flex mb-3"><k-spacer/>
							<div class="k-inline-checkbox-holder mx-2"><k-checkbox class="ma-0 pa-0" v-model="edited_resource.restricted" hide-details><template v-slot:label><div style="font-size:14px">Restrict resource to <b>authorized teachers and staff only</b></div></template></k-checkbox></div>
						<k-spacer/></div>

						<!-- Mappings to subjects/grades/courses -- used ONLY for PD resources -->
						<div class="text-center mb-3 mt-5" v-if="site_config.show_pd=='yes'">
							<k-btn small color="secondary" class="k-tight-btn k-nocaps-btn" @click="show_pd_mappings=!show_pd_mappings">Set subject/grade mappings for PD resources</k-btn>
							<div class="d-flex mt-3" v-if="show_pd_mappings">
								<k-autocomplete
									v-model="subject_mappings"
									label="Subject(s)"
									placeholder="(type to search tags)"
									hint="Required if grade entered"
									class="mb-0 pb-0 mr-1"
									background-color="#fff"
									:items="subject_tag_options"
									chips deletable-chips small-chips
									multiple dense hide-detail outlined
									persistent-hint
								/>
								<k-autocomplete
									v-model="grade_mappings"
									label="Grade(s)"
									placeholder="(type to search tags)"
									hint="Required if subject entered"
									class="mb-0 pb-0 mx-1"
									background-color="#fff"
									:items="grade_tag_options"
									chips deletable-chips small-chips
									multiple dense hide-detail outlined
									persistent-hint
								/>
								<k-autocomplete
									v-model="course_mappings"
									label="Course(s)"
									placeholder="(type to search tags)"
									class="mb-0 pb-0 ml-1"
									background-color="#fff"
									:items="course_tag_options"
									chips deletable-chips small-chips
									multiple dense hide-detail outlined
								/>
							</div>
						</div>

					</div> <!-- end of "additional tagging options" -->
				</div>
			</k-card-text>
		</k-card>
	</k-dialog>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import draggable from 'vuedraggable'
import CASEItemBtn from '../standards/CASEItemBtn'

export default {
	components: { CASEItemBtn, draggable },
	props: {
		lesson: { type: Object, required: false, default() { return null } },
		unit: { type: Object, required: false, default() { return null } },
		course: { type: Object, required: false, default() { return null } },
		original_resource: { required: true },	// if this is type string, we're creating a new resource
		shifted: { type: Boolean, required: false, default() { return false } },	// true if the editor has been shifted to one side of the screen so the resource can be previewed side-by-side
	},
	data() { return {
		dialog_open: true,
		edited_resource: null,
		subject_mappings: [],
		grade_mappings: [],
		course_mappings: [],
		resource_html: null,

		// the following are used for assessments
		blueprint_url: '',
		paper_url: '',	// "Teacher Test Booklet URL" / "Writable Extended Response TE Booklet URL"
		student_booklet_url: '',

		uploaded_file: null,
		uploaded_file_name: '',

		drag_options: {
			animation: 200,
			handle: ".k-move-handle",
		},

		new_standard_is_secondary: false,
		content_type_options: [],
		license_options: [
			{value: 'public-domain', text: 'Public Domain', url:'https://creativecommons.org/public-domain/pdm/'},
			{value: 'attribution', text: 'Attribution', url:'https://creativecommons.org/licenses/by/4.0/'},
			{value: 'attribution-ndw', text: 'Attribution-No Derivative Works', url:'https://creativecommons.org/licenses/by-nd/4.0/deed.en'},
			{value: 'attribution-sa', text: 'Attribution Share Alike', url:'https://creativecommons.org/licenses/by-sa/4.0/deed.en'},
			{value: 'attribution-nc', text: 'Attribution Non-Commercial', url:'https://creativecommons.org/licenses/by-nc/4.0/deed.en'},
			{value: 'attribution-ncsa', text: 'Attribution Non-Commercial Share Alike', url:'https://creativecommons.org/licenses/by-nc-sa/4.0/deed.en'},
			{value: 'attribution-ncnd', text: 'Attribution Non-Commercial No Derivatives', url:'https://creativecommons.org/licenses/by-nc-nd/4.0/deed.en'}
		],
		show_pd_mappings: false,
	}},
	computed: {
		...mapState(['all_courses', 'site_config']),
		additional_metadata_showing: {
			get() { return this.$store.state.lst.resource_editor_additional_metadata_showing },
			set(val) { this.$store.commit('lst_set', ['resource_editor_additional_metadata_showing', val]) }
		},
		resource_editor_case_framework_identifier: {
			get() { return this.$store.state.lst.resource_editor_case_framework_identifier },
			set(val) { this.$store.commit('lst_set', ['resource_editor_case_framework_identifier', val]) }
		},
		long_description_editor_config() {
			return U.get_froala_config({
				placeholderText: '', 
				zIndex: 1001
			})
		},
		html_resource_editor_config() {
			return U.get_froala_config({
				placeholderText: '', 
				heightMin: 200,
				zIndex: 1000
			})
		},
		resource_types() {
			let arr = []
			if (this.site_config.file_storage_system == 'equella') {
				arr.push({ value: 'equella', text: 'OpenEQUELLA Resource' })
			}
			arr.push({ value: 'website', text: 'Website' })
			arr.push({ value: 'document', text: 'Web document (e.g. Google Drive, OneDrive, etc.)' })
			arr.push({ value: 'video', text: 'Video' })
			// arr.push({ value: 'interactive', text: 'Interactive/Simulation' })
			arr.push({ value: 'upload', text: 'Uploaded File' })
			arr.push({ value: 'html', text: `HTML Resource (entered in ${this.site_config.app_name})` })
			if (this.site_config.include_assessment_resources == 'yes') {
				arr.push({ value: 'assessment', text: 'Assessment' })
			}
			return arr
		},
		edited_resource_has_extensions() {
			return U.object_has_keys(this.edited_resource.extensions)
		},
		assessment_types() {
			return [
				{value: 'illuminate', text: 'Renaissance'},
				{value: 'writable', text: 'Writable'},
			]
		},
		dialog_max_width() {
			return 820
			if (!this.edited_resource) return 640
			if (this.edited_resource.type == 'html') return 820
			if (this.additional_metadata_showing) return 820
			return 640
		},
		is_new_resource() { 
			// new resources will be marked with an `is_new_resource` flag. but this flag won't be propagated in edited_resource, because it's not part of the Resource class constructor
			return this.original_resource.is_new_resource 
		},
		resource_type() { return this.edited_resource.type },
		header_text() { return this.is_new_resource ? 'New Resource' : 'Edit Resource' },
		show_shift_resource_button() { return this.resource_type != 'none' || this.shifted },
		subject_tag_options() {
			let arr = [{value: 'subject-all', text: 'All subjects'}]
			for (let s in this.$store.state.subjects) {
				arr.push({value: ('subject-' + s).toLowerCase(), text: s})
			}
			return arr
		},
		grade_tag_options() {
			let arr = [
				{value: 'grade-all', text: 'All grades'},
				{value: 'grade-elem', text: 'Grades K-5'},
				{value: 'grade-mid', text: 'Grades 6-8'},
				{value: 'grade-high', text: 'Grades 9-12'},
			]
			for (let g of this.$store.state.grades) {
				arr.push({value: ('grade-' + g).toLowerCase(), text: 'Grade ' + g})
			}
			return arr
		},
		course_tag_options() {
			let arr = []
			for (let lp of this.all_courses) {
				if (lp.collection_type !== 'pd') arr.push({value: 'course-' + lp.course_code, text: U.html_to_text(lp.title)})
			}
			arr.sort((a,b)=>{
				if (a.text < b.text) return -1
				if (a.text > b.text) return 1
				return 0
			})

			return arr
		},
		url_field_label() {
			if (this.resource_type == 'sparkl') return 'Sparkl Activity ID'
			if (this.resource_type == 'upload') return 'Resource File Identifier'
			if (this.resource_type == 'assessment') {
				if (this.edited_resource.assessment_type == 'writable') return 'Assessment URL (optional)'
				return 'Assessment ID'
			}
			return 'Resource URL'
		},

		// checkbox for "Unit planning resource" -- only used when adding/editing resources aligned to units
		unit_planning: {
			get() { return this.edited_resource.lp_category == 'unit_planning' },
			set(val) {
				if (val) this.edited_resource.lp_category = 'unit_planning'
				else this.edited_resource.lp_category = ''
			}
		},
		show_teacher_facing_control() {
			if (this.course && this.course.collection_type === 'pd') return false
			return true
		},
		both_block_and_traditional: {
			get() { return this.edited_resource.block_or_traditional == 'both' },
			set(val) { this.edited_resource.block_or_traditional = val ? 'both' : 'traditional' }
		},
		block_or_traditional: {
			get() { return this.edited_resource.block_or_traditional == 'traditional' },
			set(val) { this.edited_resource.block_or_traditional = val ? 'traditional' : 'block' }
		},
		show_block_control() {
			if (!this.additional_metadata_showing) return false
			// show the block/traditional flag if site_config says to do so and this is a course collection
			if (this.site_config.include_block_or_traditional_setting_for_resources == 'no') return false
			if (!this.course) return false
			if (this.course.collection_type == 'course') return true
			return false
		},
		show_family_control() {
			if (!this.additional_metadata_showing) return false
			// show the block/traditional flag if site_config says to do so and this is a course or repo collection
			if (this.site_config.include_family_setting_for_resources == 'no') return false
			if (!this.course) return false
			if (this.course.collection_type == 'course' || this.course.collection_type == 'repo') return true
			return false
		},
		show_todo_control() {
			if (!this.additional_metadata_showing) return false
			// show the 'require completion' flag if this is a pd resource and show_pd is on
			if (this.site_config.show_pd == 'no') return false
			if (this.course && this.course.collection_type === 'pd') return true
			return false
		},
		show_restricted_resources_control() {
			if (!this.additional_metadata_showing) return false
			if (this.site_config.show_restricted_resources_control == 'yes') return true
			return false
		},

		// editable extensions
		license_text: {
			get() { return this.edited_resource.extensions.license_text || ''},
			set(val) {
				if (val?.hasOwnProperty('text')) val = val.text
				this.$set(this.edited_resource.extensions, 'license_text', val) 
				this.$store.commit('lst_set', ['resource_editor_license_text', val])
			}
		},
		license_uri: {
			get() { return this.edited_resource.extensions.license_uri || ''},
			set(val) { 
				this.$set(this.edited_resource.extensions, 'license_uri', val) 
				this.$store.commit('lst_set', ['resource_editor_license_uri', val])
			}
		},
		license_option() {
			// if the user chose one of the "official" licenses, this will return the object representing that license
			return this.license_options.find(x=>x.text == this.edited_resource.extensions.license_text)
		},
		license_text_label() {
			// if user has chosen an "official" license, or hasn't specified any license, call the field 'License'
			if (empty(this.edited_resource.extensions.license_text) || !empty(this.license_option)) {
				return 'License (select from menu or enter custom License text)'
			} else {
				// otherwise call it 'License Description', and add another field for License URI
				return 'License Description'
			}
		},
		content_source: {
			get() { return this.edited_resource.extensions.content_source || ''},
			set(val) { 
				this.$set(this.edited_resource.extensions, 'content_source', val) 
				this.$store.commit('lst_set', ['resource_editor_content_source', val])
			}
		},
		content_type: {
			get() { return this.edited_resource.extensions.content_type || ''},
			set(val) { 
				this.$set(this.edited_resource.extensions, 'content_type', val) 
				this.$store.commit('lst_set', ['resource_editor_content_type', val])
			},
		},
	},
	watch: {
	},
	created() {
		vapp.resource_editor_component = this

		// create edited_resource, based on original_resource (if we received one)
		let er = new Resource(this.original_resource)

		// make sure edited_resource has an extensions object
		if (empty(er.extensions)) er.extensions = {}

		// and make sure it has an assessment_type, so that it can be changed to type assessment and work properly
		if (empty(er.assessment_type)) er.assessment_type = 'illuminate'

		// retrieve resource_html if we should already have it
		if (er.type == 'html' && !empty(er.url)) {
			let html_url = '/user-files/' + er.url
			$.ajax({
				url: html_url,
				success: (html)=>{
					this.resource_html = html
					this.$nextTick(x=>this.$refs.froala_wrapper.set_model_value())
				},
				cache: false,
			})
		}

		// split mappings into groups for editing
		// only include if they match an option from the list and only if 'show_pd'
		if (this.site_config.show_pd == 'yes') {
			let subjects = this.subject_tag_options.map(x => x.value)
			let grades = this.grade_tag_options.map(x => x.value)
			let courses = this.course_tag_options.map(x => x.value)
			for (let m of er.mappings) {
				if (subjects.includes(m)) this.subject_mappings.push(m)
				if (grades.includes(m)) this.grade_mappings.push(m)
				if (courses.includes(m)) this.course_mappings.push(m)
			}

			if (this.subject_mappings.length || this.grade_mappings.length || this.course_mappings.length) {
				this.show_pd_mappings = true
			}
		}

		// for assessment, get blueprint_url, paper_url, and student_booklet_url if there
		if (er.type == 'assessment') {
			let bp = er.supp_links.find(x=>x.type=='blueprint')
			if (!empty(bp)) this.blueprint_url = bp.url

			let p = er.supp_links.find(x=>x.type=='paper')
			if (!empty(p)) this.paper_url = p.url

			let sp = er.supp_links.find(x=>x.type=='student_booklet')
			if (!empty(sp)) this.student_booklet_url = sp.url
		}

		// create array of content type options: start with the array from site_config...
		let arr = this.site_config.resource_content_type_options.concat([])
		// ... add an UNSPECIFIED option for leaving the field empty and an OTHER option for a custom type
		arr.unshift('–– UNSPECIFIED ––')
		arr.push('Other…')
		if (!empty(er.extensions.content_type) && !arr.includes(er.extensions.content_type)) {
			// and if a custom type was selected, add it
			arr.push(er.extensions.content_type)
		}
		this.content_type_options = arr

		this.edited_resource = er
	},
	mounted() {
	},
	methods: {
		upload_file() {
			// prompt for file to import
			this.$prompt({
				text: '<p>Select the file you’d like to upload:</p>',
				promptType: 'file',
				acceptText: 'Choose File',
				cancelText: 'Cancel'
			}).then(file => {
				// console.log(file)
				// TODO: check for appropriate file types -- file.type
				// TODO: check for file size -- file.size
				// make sure file has an extension
				if (file.name.search(/.*(\.\w+)$/) == -1) {
					this.$alert('Uploaded files must have a valid file name extension, such as “.pdf” or “.jpg”')
					return
				}

				this.uploaded_file = file
				this.uploaded_file_name = file.name.replace(/.*(\.\w+)$/, U.new_uuid() + '$1')

				// set url to this.uploaded_file_name
				this.edited_resource.url = this.uploaded_file_name

				// if description is empty, set to the actual file name
				if (empty(this.edited_resource.description)) {
					this.edited_resource.description = file.name
				}

			}).catch(n => {console.error(n)})
		},

		set_teacher_facing_default(new_default) {
			// remember this setting for the next new resource (ResourceSearch)
			this.$store.commit('lst_set', ['resource_editor_teacher_facing', new_default])
		},

		resource_type_selected() {
			if (this.resource_type == 'upload') {
				this.upload_file()
			}
			// remember this setting for the next new resource (ResourceSearch)
			this.$store.commit('lst_set', ['resource_editor_resource_type', this.resource_type])
		},

		content_type_changed() {
			if (this.content_type == 'Other…') {
				this.$prompt({
					title: 'Content Type',
					text: 'Enter the custom Content Type value',
					disableForEmptyValue: true,
					acceptText: 'Set Content Type',
				}).then(t => {
					t = $.trim(t)
					if (!empty(t)) {
						// add the newly-added type to the end of content_type_options
						this.content_type_options.push(t)
						this.content_type = t
					}
				}).catch(n => {console.error(n)})
			} else if (val == '–– UNSPECIFIED ––') {
				this.content_type = ''
			}
		},

		///////////////////////////////////
		// Standards
		align_to_standard(arg) {
			// use quick_align if specified in site_config (we will eventually always use quick_align)
			if (this.site_config.use_quick_align) {
				this.align_to_standard_quick(arg)
				return
			}

			let start_item = null
			if (typeof(arg) == 'string') {
				// if we get a string argument, it tells us whether aligned standards are to be primary or secondary
				this.new_standard_is_secondary = (arg == 'secondary') ? true : false

			} else {
				// else the arg is the "start item" for SatchelEmbed
				start_item = arg
			}

			let data = { framework_identifier: '', item_identifier: '' }

			if (start_item?.framework_identifier) {
				data.framework_identifier = start_item.framework_identifier
			} else {
				// if we have a resource_editor_case_framework_identifier, send it
				if (this.resource_editor_case_framework_identifier) data.framework_identifier = this.resource_editor_case_framework_identifier
			}

			if (start_item?.identifier) {
				data.item_identifier = start_item.identifier
			} else {
				// we could set some kind of default here...
			}

			// add current lesson standards as selected items
			if (this.edited_resource.standards.length > 0) {
				data.selected_items = []
				for (let standard of this.edited_resource.standards) data.selected_items.push(standard.identifier)
			}

			// set hide_fn to hide the standards chooser if/when the editor is no longer visible
			let show_data = { hide_fn: ()=>{ return ($('.k-resource-editor-outer').length == 0) } }

			vapp.$refs.satchel.execute('show', show_data).then(()=>{
				vapp.$refs.satchel.execute('load_framework', data).then(()=>{
					vapp.$refs.satchel.execute('chooser', {chooser_mode: true}).then((aligned_item) => {
						// if we already have this item aligned, remove the standard
						let i = this.edited_resource.standards.findIndex(o=>o.identifier==aligned_item.cfitem.identifier)
						if (i > -1) {
							this.edited_resource.standards.splice(i, 1)
							// re-initialize the chooser, showing the framework for the item we removed
							this.align_to_standard({framework_identifier: aligned_item.framework_identifier})

						} else {
							// Add the standard, along with the incoming framework_identifier, childOf, and sequenceNumber. use the learning progression CASE_Item structure for standards; this is a bit simpler than the full CFItem structure
							let o = new CASE_Item(aligned_item)

							// set is_secondary for the new standard
							o.is_secondary = this.new_standard_is_secondary
							console.warn('this.new_standard_is_secondary', this.new_standard_is_secondary)

							this.edited_resource.standards.push(o)

							// store current list of standards in lst.resource_editor_standards
							this.$store.commit('lst_set', ['resource_editor_standards', object_copy(this.edited_resource.standards)])

							// re-initialize the chooser, showing the framework for the item we added
							this.align_to_standard({framework_identifier: aligned_item.framework_identifier})
						}
					})
				})
			})
		},

		// align_to_standard using new "quick_align" functionality
		align_to_standard_quick(arg) {
			if (typeof(arg) == 'string') {
				// if we get a string argument, it tells us whether aligned standards are to be primary or secondary
				this.new_standard_is_secondary = (arg == 'secondary') ? true : false
			}

			let quick_align_payload = { 
				choice_fn: this.align_to_standard_choice_made
			}

			// if this is the first time we're opening the satchel quick_align interface (from any part of cureum)...
			if (!this.$store.state.quick_align_previously_opened) {
				// if we have a resource_editor_case_framework_identifier, send it
				if (this.resource_editor_case_framework_identifier) {
					quick_align_payload.framework_identifier = this.resource_editor_case_framework_identifier
				}
				// if site_config says to do so, only show outcomes as alignment options by default (the user can change this once in the satchel quick_align interface)
				if (this.site_config.align_assets_to_outcomes_only == 'yes') {
					quick_align_payload.outcomes_only = true
				}
				// TODO: fill in start_branch_identifier if we're in a collection that has a branch identifier specified...

				this.$store.commit('set', ['quick_align_previously_opened', true])
			}
			// if this *isn't* the first time we're opening the interface, we don't send this info, which causes satchel to use whatever settings the user used last time

			// send current standards in the quick_align existing_alignments format
			if (this.edited_resource.standards.length > 0) {
				quick_align_payload.existing_alignments = {}
				for (let standard of this.edited_resource.standards) {
					quick_align_payload.existing_alignments[standard.identifier] = {
						framework_identifier: standard.framework_identifier,
						cfitem: standard.copy_for_save(),
						aligned: true,
					}
				}
			}

			// if we have at least a title, send resource data in suggestion_params
			if (!empty(this.edited_resource.description)) {
				quick_align_payload.resource_universe_item = {
					resourceSourcedId: this.edited_resource.resource_id,
					title: this.edited_resource.description,
					alignmentMetadata: [this.edited_resource.long_description ?? ''],
					// educationLevel: [],
				}
			}

			// set hide_fn to hide the standards chooser if/when the editor is no longer visible
			let show_payload = { 
				start_size: 'large',
				hide_fn: ()=>{ return ($('.k-resource-editor-outer').length == 0) } 
			}

			// console.warn('new align_to_standard', quick_align_payload)

			vapp.$refs.satchel.execute('show', show_payload).then(()=>{
				vapp.$refs.satchel.execute('quick_align', quick_align_payload).then(x=>{
					// user clicked "done" from quick align interface	
				})
			})
		},

		align_to_standard_choice_made(data) {
			console.warn('cureum align_to_standard_choice_made', data)
			// if we already have this item aligned, remove the standard
			let i = this.edited_resource.standards.findIndex(o=>o.identifier==data.cfitem.identifier)
			if (i > -1) {
				this.edited_resource.standards.splice(i, 1)

			} else {
				// Add the standard, along with the incoming framework_identifier, childOf, and sequenceNumber. use the learning progression CASE_Item structure for standards; this is a bit simpler than the full CFItem structure
				let o = new CASE_Item({
					framework_identifier: data.framework_identifier,
					cfitem: data.cfitem,
					// TODO: we want to get the following too
					childOf: data.childOf,	// item identifier
					sequenceNumber: data.sequenceNumber,	// integer
				})

				// set is_secondary for the new standard
				o.is_secondary = this.new_standard_is_secondary
				// console.warn('this.new_standard_is_secondary', this.new_standard_is_secondary)

				this.edited_resource.standards.push(o)

				// store current list of standards in lst.resource_editor_standards
				this.$store.commit('lst_set', ['resource_editor_standards', object_copy(this.edited_resource.standards)])
			}
		},

		remove_standard(s, confirmed) {
			let i = this.edited_resource.standards.findIndex(o=>o.identifier==s.identifier)
			if (i > -1) {
				if (!this.confirmed_standard_delete && !confirmed) {
					this.$confirm({
						title: 'Remove Standard Alignment',
						text: 'Are you sure you want to remove this standard alignment from the resource?',
						acceptText: 'Remove',
						acceptColor: 'red',
					}).then(y => {
						this.remove_standard(s, true)
						this.confirmed_standard_delete = true
					}).catch(n => {console.error(n)})
					return
				}

				this.edited_resource.standards.splice(i, 1)

				// store current list of standards in lst.resource_editor_standards
				this.$store.commit('lst_set', ['resource_editor_standards', object_copy(this.edited_resource.standards)])
			}
		},

		drag_complete(evt) {
			// standards will have been reordered; save whenever the user clicks save
		},

		use_last_selected_standards() {
			// if we have default values for standards, set them
			if (this.$store.state.lst.resource_editor_standards.length > 0) {
				for (let s of this.$store.state.lst.resource_editor_standards) {
					this.edited_resource.standards.push(new CASE_Item(s))
				}
				// this.edited_resource.standards = object_copy(this.$store.state.lst.resource_editor_standards)
			}
		},

		use_last_selected_extensions() {
			let extensions = {}
			if (this.$store.state.lst.resource_editor_content_source) extensions.content_source = this.$store.state.lst.resource_editor_content_source
			if (this.$store.state.lst.resource_editor_content_type) extensions.content_type = this.$store.state.lst.resource_editor_content_type
			if (this.$store.state.lst.resource_editor_license_uri) extensions.license_uri = this.$store.state.lst.resource_editor_license_uri
			if (this.$store.state.lst.resource_editor_license_text) extensions.license_text = this.$store.state.lst.resource_editor_license_text
			if (U.object_has_keys(extensions)) this.edited_resource.extensions = extensions
		},

		///////////////////////////////////////////////
		save_resource(flag) {
			// combine mappings
			this.edited_resource.mappings = this.subject_mappings.concat(this.grade_mappings, this.course_mappings)

			// convert from resource_type to type list from resources.js
			if (this.resource_type == 'google_drive' || this.resource_type == 'google_assignment' || this.resource_type == 'onedrive') this.edited_resource.type = 'document'
			if (this.resource_type == 'equella') this.edited_resource.type = 'website'

			// for an assessment save supplemental links
			this.edited_resource.supp_links = []
			if (this.resource_type == 'assessment') {
				if (!empty(this.blueprint_url)) {
					this.edited_resource.supp_links.push({type: 'blueprint', url: this.blueprint_url})
				}
				if (!empty(this.paper_url)) {
					this.edited_resource.supp_links.push({type: 'paper', url: this.paper_url})
				}
				if (!empty(this.student_booklet_url)) {
					this.edited_resource.supp_links.push({type: 'student_booklet', url: this.student_booklet_url})
				}
			} else {
				// if this isn't an assessment, make sure we don't save assessment_type
				delete this.edited_resource.assessment_type
			}

			// if we don't have any extensions, don't save them
			if (!this.edited_resource_has_extensions) {
				delete this.edited_resource.extensions
			} else {
				// if the user chose one of the "official" licenses, fill in the URL
				if (!empty(this.license_option)) {
					this.edited_resource.extensions.license_uri = this.license_option.url
					// also, in this case we want to clear the lst license_uri, because we don't want that uri to be filled in accidentally for future resources
					this.$store.commit('lst_set', ['resource_editor_license_uri', ''])
				}
			}

			let uploaded_file = null

			// if type is upload, we must have a resource guid in the url field
			if (this.edited_resource.type == 'upload') {
				if (!U.is_uuid(this.edited_resource.url.replace(/\.\w+$/, ''))) {
					this.$alert('For an Uploaded File resource, you must either choose a file to upload, or enter a previously-uploaded Resource File Identifier.')
					return
				}
				// if the user specified a file to be uploaded, include it in the resource save operation
				// (the user can alternatively specify an already-existing filename -- though this is risky and will probably be undocumented)
				if (this.edited_resource.url == this.uploaded_file_name) {
					uploaded_file = this.uploaded_file
				}

			} else if (this.edited_resource.type == 'html') {
				if (empty(this.resource_html)) {
					this.$alert('Please enter the text you’d like to save for this resource.')
					return
				}
				uploaded_file = this.resource_html
				// generate a filename for the html, if not already specified
				if (!U.is_uuid(this.edited_resource.url.replace(/\.\w+$/, ''))) {
					this.edited_resource.url = U.new_uuid() + '.html'
				}

			} else {
				if (empty(this.edited_resource.url) && this.edited_resource.assessment_type != 'writable') {
					this.$alert("You must specify the " + this.url_field_label + " for the resource.")
					return
				}
			}

			if (empty(this.edited_resource.description)) {
				this.$alert('Please enter a brief title or description for the resource.')
				return
			}

			// only show warnings about mappings if site_config.show_pd=='yes'
			if (this.site_config.show_pd == 'yes') {
				if (this.subject_mappings.length > 0 && this.grade_mappings.length == 0) {
					this.$alert('When tagging resources to one or more subjects, please also choose one or more grade tags. (Choose “All grades” if the resource is relevant for all grades.)')
					return
				}
				
				if (this.grade_mappings.length > 0 && this.subject_mappings.length == 0) {
					this.$alert('When tagging resources to one or more grades, please also choose one or more subject tags. (Choose “All subjects” if the resource is relevant for all subjects.)')
					return
				}
			}

			if (this.edited_resource.publish_status && this.edited_resource.publish_status.hasOwnProperty('key')) {
				this.edited_resource.publish_status = this.edited_resource.publish_status.key
			}

			// hack/easter egg: if the type is website and the url matches "A43133", make this a sparkl activity
			if (this.edited_resource.type == 'website' && this.edited_resource.url.search(/^A(\d+)$/) > -1) {
				this.edited_resource.url = RegExp.$1
				this.edited_resource.type = 'sparkl'
				// the following two settings make it so that cureum will always block the activity from being editable when referenced by this resource; note though that the activity *will* still be editable by the creator of the activity *as registered in the Sparkl database*.
				this.edited_resource.home_collection_id = -1
				this.edited_resource.creator = -1
			}

			this.$store.dispatch('save_resource', {resource: this.edited_resource, uploaded_file: uploaded_file}).then((resource_data)=>{
				// once resource is saved, emit edit_resource_saved, sending on the resource data sent back from the service
				this.$emit('edit_resource_saved', resource_data)
			}).catch(error=>{
				console.error(error)
				this.$alert(sr('A problem occurred when attempting to save the resource. Message from server:<div class="mt-2">$1</div>', error))
			})
		},
	}
}
</script>

<style lang="scss">
.k-resource-editor-outer {
	background-color:#fbe4d4;

	.v-input--checkbox .v-label {
		color:#222;
	}
}

.k-resource-editor-dialog-shifted {
	position: fixed;
	left: auto;
	right: 0.1vw;
	top: 4px;
	width: 49.7vw;
	margin: 0;
	height: calc(100vh - 8px);
	max-height: calc(100vh - 8px) !important;
	overflow-y: auto;
}

.k-inline-checkbox-holder {
	display: inline-block;
	background-color:#fff;
	padding:4px 6px;
	border:1px solid #9E9E9E;
	border-radius:4px;
}
</style>
